import React from "react";
import Cards from "../../../components/elements/Cards";
import Badge from "../../../components/elements/Badge";
import dayjs from "dayjs";

function UserAddresses({ addresses = [] }) {
  // Sort addresses to show default first
  const sortedAddresses = [...addresses].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return 0;
  });

  const getAddressTypeBadge = (label, isDefault) => {
    const typeConfig = {
      Home: {
        color:
          "bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-blue-200",
        icon: "fa-home",
        iconColor: "text-blue-600",
      },
      Office: {
        color:
          "bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200",
        icon: "fa-building",
        iconColor: "text-emerald-600",
      },
      Work: {
        color:
          "bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 border-purple-200",
        icon: "fa-briefcase",
        iconColor: "text-purple-600",
      },
      Other: {
        color:
          "bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 border-slate-200",
        icon: "fa-map-marker-alt",
        iconColor: "text-slate-600",
      },
    };

    const config = typeConfig[label] || typeConfig["Other"];

    return (
      <div className="flex items-center gap-1 md:gap-2 flex-wrap">
        <span
          className={`inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-semibold border backdrop-blur-sm ${config.color} shadow-sm`}
        >
          <i className={`fa-solid ${config.icon} mr-2 ${config.iconColor}`}></i>
          {label}
        </span>
        {isDefault && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200 shadow-sm backdrop-blur-sm">
            <i className="fa-solid fa-crown mr-2 text-amber-600"></i>
            default
          </span>
        )}
      </div>
    );
  };

  const formatAddress = (address) => {
    const parts = [
      address?.address1,
      address?.address2,
      address?.city,
      address?.state,
      address?.country,
      address?.zipcode,
    ].filter(Boolean);

    return parts.join(", ") || "Address not specified";
  };

  if (!addresses || addresses.length === 0) {
    return (
      <Cards
        title="Address Information"
        description="No addresses have been added yet."
      >
        <div className="text-center py-16">
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mb-6">
            <i className="fa-solid fa-map-marker-alt text-3xl text-slate-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No addresses found
          </h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto">
            User add their first address to get started with deliveries and
            correspondence.
          </p>
        </div>
      </Cards>
    );
  }

  return (
    <Cards
      title="Address Information"
      description="Saved addresses for delivery and correspondence."
    >
      <div className="space-y-4">
        {sortedAddresses.map((address, index) => (
          <div
            key={address._id}
            className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
              address?.isDefault
                ? "border-amber-200 bg-amber-50/30"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            {/* Header with name and badges */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 text-lg mb-2">
                  {address?.name || "Unnamed Address"}
                </h4>
                {getAddressTypeBadge(address?.label, address?.isDefault)}
              </div>

              {/* Action dots (for future use) */}
              {/* <div className="flex items-center">
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </button>
              </div> */}
            </div>

            {/* Address details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-gray-500 mt-1 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Address
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {formatAddress(address)}
                    </p>
                  </div>
                </div>

                {address?.landmark && (
                  <div className="flex items-start gap-3">
                    <i className="fa-solid fa-map-pin text-gray-500 mt-1 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Landmark
                      </p>
                      <p className="text-sm text-gray-600">
                        {address?.landmark}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {address?.phone && (
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-phone text-gray-500 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </p>
                      <p className="text-sm text-gray-600">{address?.phone}</p>
                    </div>
                  </div>
                )}

                {address?.email && (
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-envelope text-gray-500 flex-shrink-0"></i>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Email
                      </p>
                      <p className="text-sm text-gray-600">{address?.email}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-calendar text-gray-500 flex-shrink-0"></i>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Added
                    </p>
                    <p className="text-sm text-gray-600">
                      {dayjs(address?.createdAt).format("DD MMM, YYYY")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coordinates info if available */}
            {address?.coordinates && address?.coordinates?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1 md:gap-2 text-xs text-gray-500">
                  <i className="fa-solid fa-crosshairs"></i>
                  <span>
                    GPS Coordinates: {address?.coordinates.join(", ")}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Cards>
  );
}

// Example usage with demo data
function UserAddress({ addresses = [] }) {
  console.log(addresses);
  return <UserAddresses addresses={addresses} />;
}

export default UserAddress;
